'use client';

import React, { useState } from 'react';
import { Crown, Check, Zap, Lock } from 'lucide-react';
import PremiumModal from '@/components/PremiumModal';
import { useLanguage } from '@/context/LanguageContext';
import { getTranslations } from '@/lib/i18n';

export default function PremiumSection() {
  const [premiumOpen, setPremiumOpen] = useState(false);
  const { language } = useLanguage();
  const t = getTranslations(language);

  return (
    <>
      <section id="premium" className="py-20 relative overflow-hidden">
        {/* Background */}
        <div
          className="hero-glow-orb w-[600px] h-[600px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-10"
          style={{ background: 'var(--primary)' }}
        />

        <div className="relative z-10 max-w-screen-2xl mx-auto px-6 lg:px-10 xl:px-16">
          {/* Header */}
          <div className="text-center mb-14">
            <p className="section-header mb-3">{t.premium.sectionLabel}</p>
            <h2 className="font-extrabold text-4xl md:text-5xl mb-4">
              <span className="shimmer-text">{t.premium.headline1}</span>
              <span style={{ color: 'var(--foreground)' }}> {t.premium.headline2}</span>
            </h2>
            <p className="text-lg max-w-xl mx-auto" style={{ color: 'var(--muted-foreground)' }}>
              {t.premium.subtitle}
            </p>
          </div>

          {/* Plans */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {/* Free */}
            <div
              className="rounded-2xl p-6"
              style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
            >
              <div className="mb-5">
                <p className="text-sm font-semibold mb-1" style={{ color: 'var(--muted-foreground)' }}>{t.premium.free}</p>
                <div className="flex items-baseline gap-1">
                  <span className="font-extrabold text-4xl">0 €</span>
                </div>
                <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>{t.premium.forever}</p>
              </div>
              <ul className="space-y-2.5 mb-6">
                {t.premium.freeFeatures?.map((f: string) => (
                  <li key={`free-${f}`} className="flex items-center gap-2 text-sm">
                    <Check size={14} style={{ color: 'var(--neon-green)' }} />
                    {f}
                  </li>
                ))}
                <li className="flex items-center gap-2 text-sm" style={{ color: 'var(--muted-foreground)' }}>
                  <Lock size={14} />
                  {t.premium.spicyLocked}
                </li>
                <li className="flex items-center gap-2 text-sm" style={{ color: 'var(--muted-foreground)' }}>
                  <Lock size={14} />
                  {t.premium.chaosLocked}
                </li>
              </ul>
              <button
                className="w-full py-3 rounded-xl text-sm font-semibold transition-all hover:bg-white/5"
                style={{ border: '1px solid var(--border)', color: 'var(--muted-foreground)' }}
              >
                {t.premium.playFree}
              </button>
            </div>

            {/* Day Pass */}
            <div
              className="rounded-2xl p-6 relative"
              style={{
                background: 'var(--card)',
                border: '1px solid rgba(124,58,237,0.4)',
                boxShadow: '0 0 40px rgba(124,58,237,0.15)',
              }}
            >
              <div className="mb-5">
                <p className="text-sm font-semibold mb-1" style={{ color: 'var(--neon-violet)' }}>{t.premium.dayPass}</p>
                <div className="flex items-baseline gap-1">
                  <span className="font-extrabold text-4xl">2,99 €</span>
                  <span className="text-sm" style={{ color: 'var(--muted-foreground)' }}>/Tag</span>
                </div>
                <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>{t.premium.dayPassDesc}</p>
              </div>
              <ul className="space-y-2.5 mb-6">
                {t.premium.premiumFeatures?.map((f: string) => (
                  <li key={`day-${f}`} className="flex items-center gap-2 text-sm">
                    <Check size={14} style={{ color: 'var(--neon-violet)' }} />
                    {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => setPremiumOpen(true)}
                className="w-full btn-primary flex items-center justify-center gap-2 py-3 text-sm font-bold rounded-xl"
              >
                <Zap size={14} />
                {t.premium.getDay}
              </button>
            </div>

            {/* Weekly — Featured */}
            <div
              className="rounded-2xl p-6 relative overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, rgba(124,58,237,0.2), rgba(245,158,11,0.1))',
                border: '2px solid var(--accent)',
                boxShadow: '0 0 60px rgba(245,158,11,0.2)',
              }}
            >
              <span
                className="absolute top-4 right-4 text-xs font-bold px-3 py-1 rounded-full"
                style={{ background: 'var(--accent)', color: 'var(--accent-foreground)' }}
              >
                {t.premium.popular}
              </span>
              <div className="mb-5">
                <p className="text-sm font-semibold mb-1" style={{ color: 'var(--neon-amber)' }}>{t.premium.weekPass}</p>
                <div className="flex items-baseline gap-1">
                  <span className="font-extrabold text-4xl">5,99 €</span>
                  <span className="text-sm" style={{ color: 'var(--muted-foreground)' }}>/Woche</span>
                </div>
                <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>{t.premium.weekPassDesc}</p>
              </div>
              <ul className="space-y-2.5 mb-6">
                {t.premium.premiumFeatures?.map((f: string) => (
                  <li key={`week-${f}`} className="flex items-center gap-2 text-sm">
                    <Check size={14} style={{ color: 'var(--neon-amber)' }} />
                    {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => setPremiumOpen(true)}
                className="w-full btn-accent flex items-center justify-center gap-2 py-3 text-sm font-bold rounded-xl"
              >
                <Crown size={14} />
                {t.premium.getWeek}
              </button>
            </div>
          </div>

          {/* Trust badges */}
          <div className="flex flex-wrap justify-center gap-6 mt-10">
            {[
              { icon: '🔒', label: t.premium.trust1 },
              { icon: '↩️', label: t.premium.trust2 },
              { icon: '⚡', label: t.premium.trust3 },
              { icon: '🇩🇪', label: t.premium.trust4 },
            ]?.map((badge) => (
              <div key={`trust-${badge?.label}`} className="flex items-center gap-2 text-sm" style={{ color: 'var(--muted-foreground)' }}>
                <span>{badge?.icon}</span>
                <span>{badge?.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      <PremiumModal open={premiumOpen} onClose={() => setPremiumOpen(false)} />
    </>
  );
}